<template>
  <div class="login-wrapper d-flex align-items-center justify-content-center">
    <!-- Language Switch -->
    <div class="lang-switch">
      <button
        class="btn btn-sm"
        :class="lang === 'fa' ? 'btn-success' : 'btn-outline-success'"
        @click="setLang('fa')"
      >
        FA
      </button>
      <button
        class="btn btn-sm"
        :class="lang === 'en' ? 'btn-success' : 'btn-outline-success'"
        @click="setLang('en')"
      >
        EN
      </button>
    </div>

    <div class="login-card text-center">
      <h3 class="mb-4 fw-semibold">
        {{ currentLang.welcome }}
      </h3>

      <!-- Avatar -->
      <div class="avatar mx-auto mb-4">
        <!-- <img src="" alt="avatar" /> -->
        <img :src="logo" alt="logo" />
      </div>

      <!-- Form -->
      <form v-if="step === 'phone'" @submit.prevent="submit">
        <div class="form-floating mb-3">
          <input
            type="tel"
            class="form-control custom-input"
            id="phone"
            maxlength="11"
            inputmode="numeric"
            :placeholder="currentLang.phone"
            v-model="phone"
            style="direction: ltr; text-align: left"
          />
          <label class="lang-switch-label mb-4" for="phone">{{
            currentLang.phone
          }}</label>
        </div>

        <div v-if="phoneError" class="error-message">
          {{ currentLang[phoneError] }}
        </div>

        <div v-if="serverError" class="error-message">
          {{ serverMessageError }}
        </div>

        <div class="form-floating mb-4"></div>

        <button
          :disabled="loading"
          type="submit"
          class="btn btn-success w-100 rounded-pill py-2"
        >
          {{ currentLang.login }}
        </button>
      </form>

      <div v-if="step === 'otp'">
        <p class="fw-bold mb-2">{{ phone }}</p>
        <div class="form-floating mb-4">
          <button
            class="btn btn-sm edit-phone-btn d-inline-flex align-items-center gap-2"
            @click="editPhone"
          >
            ✎ {{ currentLang.editPhone }}
          </button>
        </div>

        <!-- OTP boxes -->
        <div class="otp-wrapper mb-3">
          <input
            v-for="(digit, index) in digits"
            :key="index"
            ref="inputs"
            v-model="digits[index]"
            maxlength="1"
            class="otp-input"
            @input="onInput(index)"
            @keydown.backspace="onBackspace(index)"
            @paste="onPaste"
          />
        </div>

        <p v-if="otpError" class="text-danger mt-2 small">
          {{ otpError }}
        </p>
        

        <button
          :disabled="loading"
          class="btn btn-success w-100 rounded-pill py-2"
          @click="verifyOtp"
        >
          {{ currentLang.verify }}
        </button>
      </div>
    </div>
  </div>
</template>



<script setup>
/* eslint-disable vue/multi-word-component-names */
import { ref, computed, watch, nextTick } from "vue";
import logo from "@/assets/logo.jpg";
import { useRouter } from "vue-router";
import { lang, setLang } from "@/plugins/lang";
const loading = ref(false);
import enAuth from "@/locales/en/login";
import faAuth from "@/locales/fa/login";
import api from "@/plugins/axios";
const step = ref("phone"); // phone | otp
const router = useRouter();
const translations = {
  en: enAuth,
  fa: faAuth,
};
const currentLang = computed(() => translations[lang.value]);

const phone = ref("");
const phoneError = ref("");
const serverMessageError = ref("");
const serverError = ref(false);

function validatePhone() {
  const phoneRegex = /^09\d{9}$/;

  if (!phone.value) {
    phoneError.value = "requiredPhone";
    return false;
  }

  if (!phoneRegex.test(phone.value)) {
    phoneError.value = "invalidPhone";
    return false;
  }

  phoneError.value = "";
  return true;
}

const submit = async () => {
  serverError.value = false;
  serverMessageError.value = "";
  if (!validatePhone()) return;
  loading.value = true;

  try {
    const response = await api.post("/general/send-otp-code", {
      mobile: phone.value.trim(),
    });

    if (response.status === 200) {
      step.value = "otp"; // <- فعال کردن بخش OTP
      focusFirstOtp();
    }
  } catch (error) {
    serverError.value = true;
    serverMessageError.value = error.response?.data?.message || "networkError";
  } finally {
    loading.value = false;
  }
};

const digits = ref(["", "", "", ""]);
const inputs = ref([]);
const otpError = ref("");
const otpCode = ref("");

watch(digits.value, () => {
  otpCode.value = digits.value.join("");
});

function focusFirstOtp() {
  nextTick(() => {
    if (inputs.value[0]) inputs.value[0].focus();
  });
}

const editPhone = () => {
  step.value = "phone";
  digits.value = ["", "", "", ""];
  otpError.value = "";
};

const onInput = (index) => {
  const v = digits.value[index];

  if (!/^\d$/.test(v)) {
    digits.value[index] = "";
    return;
  }
  if (index < 3) {
    nextTick(() => inputs.value[index + 1].focus());
  }
};

const onBackspace = (index) => {
  if (!digits.value[index] && index > 0) {
    inputs.value[index - 1].focus();
  }
};

const onPaste = (e) => {
  const text = e.clipboardData.getData("text").slice(0, 4);
  if (!/^\d+$/.test(text)) return;

  text.split("").forEach((n, i) => {
    digits.value[i] = n;
  });
};

const verifyOtp = async () => {
  if (otpCode.value.length !== 4) {
    otpError.value = "invalidOtp";
    return;
  }

  loading.value = true;
  otpError.value = "";

  try {
    const res  = await api.post("/general/login", {
      mobile: phone.value,
      code: otpCode.value,
    });

    if (res.data.success === true && res.data.status === 200) {
      const user = res.data.data.user;
      const token = res.data.data.access_token;

      localStorage.setItem("auth_token", token);
      localStorage.setItem("user_info", JSON.stringify(user));

      router.push({ name: "AdminDashboard" });
      return;
    }


    // otpError.value = res.data.message || "خطای نامشخص";
  } catch (error) {

    if (error.response) {
      otpError.value = error.response.data.message || "خطای نامشخص";
    } else {
      otpError.value = error;
    }
  } finally {
    loading.value = false;

  }
};


</script>

<style scoped>
.login-wrapper {
  min-height: 100vh;
  background-color: #ffffff;
  position: relative;
}

/* Language Switch */
.lang-switch {
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 6px;
}

[dir="ltr"] .lang-switch {
  right: auto;
  left: 20px;
}

.lang-switch-label {
  left: auto;
  right: 0px;
  text-align: right;
}

[dir="ltr"] .lang-switch-label {
  right: auto;
  left: 0px;
  text-align: left;
}

/* Card */
.login-card {
  width: 100%;
  max-width: 380px;
  padding: 2rem;
}

/* Avatar */
.avatar {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Inputs */
.custom-input {
  border: none;
  border-bottom: 1px solid #ddd;
  border-radius: 0;
  box-shadow: none !important;
}

.custom-input:focus {
  border-bottom-color: #198754;
}

/* Button */
.btn-success {
  box-shadow: 0 8px 20px rgba(25, 135, 84, 0.35);
}

.error-message {
  background-color: #ffe6e6; /* قرمز بسیار ملایم */
  color: #dc3545; /* قرمز اصلی Bootstrap */
  border-left: 4px solid #dc3545;
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 6px rgba(220, 53, 69, 0.15);
  animation: fadeIn 0.25s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* OTP input */
.otp-wrapper {
  direction: ltr;
  display: flex;
  justify-content: center;
  gap: 10px;
}

.otp-input {
  direction: ltr;
  text-align: center;
  width: 45px;
  height: 55px;
  font-size: 22px;
  border: 1px solid #ccc;
  border-radius: 8px;
}

.edit-phone-btn {
  border: 1px solid #198754;
  color: #198754;
  background-color: transparent;
  border-radius: 50px;
  padding: 6px 14px;
  font-weight: 500;
  transition: all 0.25s ease;
}

/* Hover */
.edit-phone-btn:hover {
  background-color: #198754;
  color: #fff;
  box-shadow: 0 4px 12px rgba(25, 135, 84, 0.25);
}

/* Active */
.edit-phone-btn:active {
  transform: scale(0.97);
  box-shadow: none;
}

/* موبایل */
@media (max-width: 576px) {
  .edit-phone-btn {
    padding: 4px 10px;
    font-size: 13px;
  }
}
</style>
